import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { TNodeUpdate, TTimelineItem } from "../types";
import { addChild, removeNode, updateNode } from "../utils/tree";

export interface TimelineDataStoreCallbacks {
  onNodeUpdate?: (nodeId: string, updates: TNodeUpdate) => void;
  onNodeAdd?: (parentId: string | null, node: TTimelineItem) => void;
  onNodeRemove?: (nodeId: string) => void;
}

export interface TimelineDataStore {
  items: TTimelineItem[];
  durationSeconds: number;

  // Bulk sync from props
  setItems: (items: TTimelineItem[]) => void;
  setDuration: (duration: number) => void;

  // Node-id-based CRUD — every node in the tree (tracks, collections, segments,
  // video) has a unique id; callers address nodes by that id alone.
  addNode: (parentId: string | null, node: TTimelineItem) => void;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, updates: TNodeUpdate) => void;
}

export const createTimelineDataStore = (
  initialItems: TTimelineItem[],
  initialDuration: number,
  callbacks?: TimelineDataStoreCallbacks,
) => {
  return create<TimelineDataStore>()(
    devtools(
      (set) => ({
        items: initialItems,
        durationSeconds: initialDuration,

        setItems: (items) => set({ items }),
        setDuration: (duration) => set({ durationSeconds: duration }),

        addNode: (parentId, node) => {
          set((state) => ({ items: addChild(state.items, parentId, node) }));
          callbacks?.onNodeAdd?.(parentId, node);
        },

        removeNode: (nodeId) => {
          set((state) => ({ items: removeNode(state.items, nodeId) }));
          callbacks?.onNodeRemove?.(nodeId);
        },

        updateNode: (nodeId, updates) => {
          set((state) => ({ items: updateNode(state.items, nodeId, updates) }));
          callbacks?.onNodeUpdate?.(nodeId, updates);
        },
      }),
      { name: "timelineDataStore" },
    ),
  );
};
